import React, { useEffect, useState } from 'react';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';
import { localStorageItem } from 'src/app/modules/utils/LocalStorage';
import { SignatureItem } from '../types';
import SignatureModal from './SignatureModal';

interface SignatureOverlayProps {
	signatureItem: SignatureItem;
	onSignature?: (signatureData: string) => void;
	documentScale: number;
}

const SignatureOverlay = ({
	signatureItem,
	onSignature,
	documentScale,
}: SignatureOverlayProps) => {
	const [showSignatureModal, setShowSignatureModal] = useState(false);
	const [signatureData, setSignatureData] = useState<string>('');
	const [isSigned, setIsSigned] = useState(false);

	const fontSizeBaseScale = `clamp(0.5rem, calc(0.75rem * ${documentScale}), 1.5rem)`;

	useEffect(() => {
		setIsSigned(Boolean(signatureItem?.value));
		setSignatureData(signatureItem?.value ?? '');
	}, [signatureItem?.value]);

	const handleSignatureClick = () => {
		// Single click - do nothing, wait for double click
		return;
	};

	const handleSignatureDoubleClick = async () => {
		if (isSigned) {
			// If already signed, double-click to reset and re-sign
			resetSignature();
			return;
		}

		// Always attempt automatic signing on double-click
		const configSignature = getConfigSignature();
		if (!configSignature) {
			// No config signature available, show modal to create one
			setShowSignatureModal(true);
			return;
		}

		// Always just embed the signature image
		// USB signing will be triggered automatically when ALL signatures are completed
		// (handled in FilePreview component via onAllSignaturesCompleted callback)
		setSignatureData(configSignature);
		setIsSigned(true);
		onSignature?.(configSignature);
	};

	const resetSignature = () => {
		setSignatureData('');
		setIsSigned(false);
		onSignature?.(''); // Clear signature data
	};

	const confirmSignature = (signatureBase64: string) => {
		setSignatureData(signatureBase64);
		setIsSigned(true);
		onSignature?.(signatureBase64);
		setShowSignatureModal(false);
	};

	const getConfigSignature = () => {
		return localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA)?.field7;
	};

	return (
		<>
			<div
				style={{
					position: 'relative',
					height: '100%',
					border: isSigned ? '2px solid var(--kt-success)' : '2px dashed #007bff',
					backgroundColor: isSigned
						? 'rgba(255, 255, 255, 0.95)'
						: 'rgba(0, 123, 255, 0.1)',
					cursor: isSigned ? 'pointer' : 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: fontSizeBaseScale,
					color: isSigned ? '#28a745' : '#007bff',
					borderRadius: '4px',
					backdropFilter: 'blur(50px)',
				}}
				onClick={handleSignatureClick}
				onDoubleClick={handleSignatureDoubleClick}
				title={
					isSigned
						? 'Đã ký - Double-click để ký lại'
						: 'Double-click để ký'
				}
			>
				{isSigned ? (
					<div className="text-center w-100 h-100">
						<img
							src={signatureData}
							alt="Chữ ký"
							style={{
								maxWidth: '100%',
								height: '100%',
								objectFit: 'contain',
							}}
						/>
						<div
							className="mt-1"
							style={{ fontSize: fontSizeBaseScale, color: '#28a745' }}
						>
							Nhấn 2 lần để ký lại
						</div>
					</div>
				) : (
					<div className="text-center">
						<i
							className="bi bi-pencil text-primary"
							style={{
								fontSize: fontSizeBaseScale,
							}}
						></i>
						<div
							style={{
								fontSize: fontSizeBaseScale,
							}}
						>
							Vị trí ký
						</div>
					</div>
				)}
			</div>

			{/* Manual signature modal */}
			{showSignatureModal && (
				<SignatureModal
					open={showSignatureModal}
					onClose={() => setShowSignatureModal(false)}
					onConfirm={confirmSignature}
				/>
			)}
		</>
	);
};

export default SignatureOverlay;
